import React, { FC, useState, useMemo } from 'react'
import { Menu, MenuItem } from '@mui/material'
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Sensor, SensorState } from 'src/api'
import { useSensorLabel, useSensorStateUpdate } from 'src/hooks/ui'
import { useAssignSensorRequest } from 'src/hooks/api'
import { useDialog } from 'src/hooks/ui'
import { DialogNames, SensorStateList } from 'src/enums'
import { MakeAvailableSensorModal, MarkLostSensorModal, AssignSensorToContractor, StatefulDialog } from 'src/components'
import { SensorQualityControlDialog } from '../sensorQualityControl'

export enum SensorMenuItemKey {
  DownloadLabel = 'downloadLabel',
  ReadyToUse = 'readyToUse',
  MarkAsLost = 'markAsLost',
  MakeAvailable = 'makeAvailable',
  Assign = 'assign',
  Reassign = 'reassign',
  Delete = 'delete'
}

interface SensorContextMenuProps {
  menuItems: SensorMenuItemKey[]
  currentRow: Sensor | null
  menuAnchorEl: HTMLElement | null
  onOpenMenu: (event: React.MouseEvent<HTMLElement>, row: Sensor) => void
  onCloseMenu: () => void
  states: SensorState[]
  refetchSensorList: () => void
  onDelete?: (id: number | string) => void
  isDeleteLoading?: boolean
  /**
   * Show the quality control report before the contractor picker on Assign / Reassign.
   * Opt-in because the checks are CLiMIT-specific - the LoRaWan lists reuse this menu.
   */
  qualityControlBeforeAssign?: boolean
}

export const SensorContextMenu: FC<SensorContextMenuProps> = ({
  menuItems,
  currentRow,
  menuAnchorEl,
  onOpenMenu,
  onCloseMenu,
  states,
  refetchSensorList,
  onDelete,
  isDeleteLoading = false,
  qualityControlBeforeAssign = false
}) => {
  const { generatingLabel, downloadLabel } = useSensorLabel()
  const { openSensorMarkLostDialog, closeSensorMarkLostDialog, openSensorMakeAvailableDialog, closeSensorMakeAvailableDialog, updateSensorState, isLoadingChangingSensorState } =
    useSensorStateUpdate()
  const { openDialog, closeDialog } = useDialog(DialogNames.AssignSensorToContractor)
  const { openDialog: openQualityControlDialog, closeDialog: closeQualityControlDialog } = useDialog(DialogNames.AssignSensorsQualityControl)
  const [sensorUniqueId, setSensorUniqueId] = useState<string | null>(null)

  const assignSensorRequest = useAssignSensorRequest({
    options: {
      onSettled: () => closeDialog(),
      onSuccess: () => refetchSensorList()
    }
  })

  const availableState = useMemo(() => states.find((state) => state.name === SensorStateList.Available), [states])
  const newState = useMemo(() => states.find((state) => state.name === SensorStateList.New), [states])
  const lostState = useMemo(() => states.find((state) => state.name === SensorStateList.Lost), [states])
  const assignedState = useMemo(() => states.find((state) => state.name === SensorStateList.Assigned), [states])

  const isLost = useMemo(() => currentRow && currentRow.stateId === lostState?.id, [currentRow, lostState])
  const disabledReadyToUse = !availableState || isLoadingChangingSensorState

  const assignSensor = (contractorId: number | null) => {
    if (sensorUniqueId !== null) {
      assignSensorRequest.mutate({
        uniqueId: sensorUniqueId,
        payload: { contractorId }
      })
    }
  }

  const handleDownloadLabel = () => {
    if (currentRow?.deviceId) {
      downloadLabel(currentRow.deviceId, currentRow?.type?.name)
      onCloseMenu()
    }
  }

  const handleReadyToUse = () => {
    if (!disabledReadyToUse && currentRow && availableState) {
      updateSensorState({
        uniqueId: currentRow.uniqueId as string,
        state: availableState as SensorState,
        onSuccess: () => {
          refetchSensorList()
          onCloseMenu()
        }
      })
    }
  }

  const handleMarkAsLost = () => {
    openSensorMarkLostDialog()
  }

  const handleMakeAvailable = () => {
    openSensorMakeAvailableDialog()
  }

  // Assign and Reassign share the same flow: quality control first (when enabled), then the
  // contractor picker. The menu closes immediately, so the sensor is captured in state first.
  const startAssignFlow = (uniqueId: string) => {
    setSensorUniqueId(uniqueId)
    if (qualityControlBeforeAssign) openQualityControlDialog()
    else openDialog()
    onCloseMenu()
  }

  const handleAssign = () => {
    if (currentRow) startAssignFlow(currentRow.uniqueId as string)
  }

  const handleReassign = () => {
    if (assignedState && currentRow) startAssignFlow(currentRow.uniqueId as string)
  }

  const handleDelete = () => {
    if (currentRow?.id && onDelete) {
      onDelete(currentRow.id)
    }
  }

  const renderMenuItem = (key: SensorMenuItemKey) => {
    switch (key) {
      case SensorMenuItemKey.DownloadLabel:
        return (
          <MenuItem key={key} disabled={generatingLabel} onClick={handleDownloadLabel}>
            Download label
          </MenuItem>
        )
      case SensorMenuItemKey.ReadyToUse:
        return (
          <MenuItem key={key} disabled={disabledReadyToUse} onClick={handleReadyToUse}>
            Ready to use
          </MenuItem>
        )
      case SensorMenuItemKey.MarkAsLost:
        return (
          <MenuItem key={key} disabled={isLoadingChangingSensorState} onClick={handleMarkAsLost}>
            Mark as lost
          </MenuItem>
        )
      case SensorMenuItemKey.MakeAvailable:
        return (
          <MenuItem key={key} disabled={isLoadingChangingSensorState} onClick={handleMakeAvailable}>
            Make available
          </MenuItem>
        )
      case SensorMenuItemKey.Assign:
        return (
          <MenuItem key={key} disabled={!assignedState} onClick={handleAssign}>
            Assign
          </MenuItem>
        )
      case SensorMenuItemKey.Reassign:
        return (
          <MenuItem key={key} disabled={!assignedState} onClick={handleReassign}>
            Reassign
          </MenuItem>
        )
      case SensorMenuItemKey.Delete:
        return (
          <MenuItem key={key} disabled={isDeleteLoading || !currentRow?.id} onClick={handleDelete}>
            Delete
          </MenuItem>
        )
      default:
        return null
    }
  }

  const getMenuItems = () => {
    if (menuItems.includes(SensorMenuItemKey.MarkAsLost) && menuItems.includes(SensorMenuItemKey.MakeAvailable)) {
      if (!isLost) {
        return menuItems.filter((key) => key !== SensorMenuItemKey.MakeAvailable).map((key) => renderMenuItem(key))
      } else {
        return menuItems.filter((key) => key !== SensorMenuItemKey.MarkAsLost).map((key) => renderMenuItem(key))
      }
    }

    return menuItems.map((key) => renderMenuItem(key))
  }

  return (
    <>
      <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={onCloseMenu}>
        {getMenuItems()}
      </Menu>
      {menuItems.includes(SensorMenuItemKey.MarkAsLost) && (
        <MarkLostSensorModal
          sensorDeviceId={currentRow?.deviceId as string}
          isLoading={isLoadingChangingSensorState}
          sensorStateName={currentRow?.state?.name}
          contractorName={currentRow?.contractorSensors?.find((cs) => cs.isActive)?.contractor?.name}
          projectName={currentRow?.projectSectionsSensors?.find((pss) => pss.isActive)?.project?.name}
          onCancel={() => {
            closeSensorMarkLostDialog()
            onCloseMenu()
          }}
          onSubmit={() => {
            if (currentRow && lostState) {
              updateSensorState({
                uniqueId: currentRow.uniqueId as string,
                state: lostState as SensorState,
                onSuccess: () => {
                  refetchSensorList()
                  onCloseMenu()
                }
              })
            }
          }}
        />
      )}
      {menuItems.includes(SensorMenuItemKey.MakeAvailable) && (
        <MakeAvailableSensorModal
          sensorDeviceId={currentRow?.deviceId as string}
          isLoading={isLoadingChangingSensorState}
          onCancel={() => {
            closeSensorMakeAvailableDialog()
            onCloseMenu()
          }}
          onSubmit={() => {
            if (currentRow && newState) {
              updateSensorState({
                uniqueId: currentRow.uniqueId as string,
                state: newState as SensorState,
                onSuccess: () => {
                  refetchSensorList()
                  onCloseMenu()
                }
              })
            }
          }}
        />
      )}
      {(menuItems.includes(SensorMenuItemKey.Assign) || menuItems.includes(SensorMenuItemKey.Reassign)) && (
        <>
          {qualityControlBeforeAssign && (
            <SensorQualityControlDialog
              name={DialogNames.AssignSensorsQualityControl}
              uniqueIds={sensorUniqueId ? [sensorUniqueId] : []}
              title="Quality Control - review before assigning"
              confirmLabel="Continue"
              onConfirm={() => closeQualityControlDialog(openDialog)}
              onClose={() => closeQualityControlDialog()}
            />
          )}
          <StatefulDialog name={DialogNames.AssignSensorToContractor} wrapContent={false}>
            <AssignSensorToContractor onSubmit={assignSensor} />
          </StatefulDialog>
        </>
      )}
    </>
  )
}

export const useSensorContextMenu = () => {
  const [currentRow, setCurrentRow] = useState<Sensor | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: Sensor) => {
    setCurrentRow(row)
    setMenuAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setCurrentRow(null)
    setMenuAnchorEl(null)
  }

  return {
    currentRow,
    menuAnchorEl,
    handleOpenMenu,
    handleCloseMenu
  }
}

export const createActionsColumn = (handleOpenMenu: (event: React.MouseEvent<HTMLElement>, row: any) => void): GridColDef => ({
  field: 'actions',
  headerName: '',
  type: 'actions',
  width: 30,
  getActions: ({ row }) => [
    <GridActionsCellItem key="actions" icon={<MoreVertIcon />} label="Actions" className="textPrimary" onClick={(e: any) => handleOpenMenu(e, row)} color="inherit" />
  ]
})
